/**
 * WordBusyStrip — a small pill saying the add-in is writing to the document.
 *
 * On Word desktop every add-in write runs on Word's UI thread, so while one is
 * in flight Word itself can lag or ignore typing. Without a sign the writer
 * reads that as Word hanging. The pill only appears once a write has run for
 * ``SHOW_AFTER_MS`` (short writes never show it) and stays at least
 * ``MIN_VISIBLE_MS`` so it can't flicker. Reads never show it.
 *
 * The parent hides it where the pane already narrates the work (the Load and
 * Save cards, a drafting run's spine).
 */

import { Spinner, makeStyles, tokens } from "@fluentui/react-components";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { subscribeWordWriteActivity } from "../../services/wordWriteQueue";

const SHOW_AFTER_MS = 1000;
const MIN_VISIBLE_MS = 600;

/** The label to show while a write has been running long enough, else null. */
export function useSlowWordWrite(): string | null {
  const [shown, setShown] = useState<string | null>(null);
  const showTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shownAt = useRef(0);
  const label = useRef<string | null>(null);

  useEffect(() => {
    const clear = (t: typeof showTimer) => {
      if (t.current !== null) clearTimeout(t.current);
      t.current = null;
    };
    const unsubscribe = subscribeWordWriteActivity(({ busy, label: next }) => {
      if (busy) {
        // Keep the last label through the gap between two queued writes.
        if (next) label.current = next;
        clear(hideTimer);
        if (shownAt.current > 0) {
          setShown(label.current ?? "");
        } else if (showTimer.current === null) {
          showTimer.current = setTimeout(() => {
            showTimer.current = null;
            shownAt.current = Date.now();
            setShown(label.current ?? "");
          }, SHOW_AFTER_MS);
        }
        return;
      }
      clear(showTimer);
      label.current = null;
      if (shownAt.current === 0) return;
      const wait = Math.max(0, MIN_VISIBLE_MS - (Date.now() - shownAt.current));
      clear(hideTimer);
      hideTimer.current = setTimeout(() => {
        hideTimer.current = null;
        shownAt.current = 0;
        setShown(null);
      }, wait);
    });
    return () => {
      unsubscribe();
      clear(showTimer);
      clear(hideTimer);
    };
  }, []);

  return shown;
}

const useStyles = makeStyles({
  pill: {
    position: "fixed",
    bottom: "12px",
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 1000,
    maxWidth: "calc(100vw - 32px)",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 12px",
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    boxShadow: tokens.shadow8,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    pointerEvents: "none",
  },
});

export function WordBusyStrip({ label }: { label: string }): React.ReactElement {
  const styles = useStyles();
  return (
    <output className={styles.pill} aria-live="polite">
      <Spinner size="extra-tiny" />
      <span>{label || "Updating the document"}… Word may be slow for a moment.</span>
    </output>
  );
}
